/**
 * Etichette di WhatsApp Business (sessioni WAHA) collegate alle etichette del CRM.
 *
 * - In uscita: quando nel CRM si assegna/toglie un'etichetta COLLEGATA (config_id +
 *   waha_label_id, migration 20260926g) a una chat WhatsApp, si aggiorna anche il telefono
 *   con PUT /api/{session}/labels/chats/{chatId}/ (WAHA vuole la lista completa: si legge
 *   prima quella attuale). Etichette solo CRM: nessuna chiamata.
 * - Importazione iniziale: GET /api/{session}/labels + GET /api/{session}/labels/{id}/chats.
 * - In arrivo: lo fa il core (inbound/handlers/label.ts) con gli eventi label.*.
 *
 * Anti ciclo: il PUT genera eventi label.chat.* che il core applica al DB in modo
 * idempotente (upsert/delete); il core non chiama mai WAHA, quindi nulla torna indietro.
 *
 * Endpoint verificati su https://waha.devlike.pro/docs/how-to/labels/ (26/09/2026).
 * Tutto passa dal proxy (waha-proxy), come in wahaApiService.
 */

import { supabase } from '../../supabase';
import { callWahaProxy } from '../wahaProxyService';
import {
  isLinkedLabel, nextWahaLabelIds, parseChatIds, parseWahaLabels, planImportLabel,
  sameIdSet, shouldSyncLabelToWhatsApp, toCrmChatJid, type LinkableLabel,
} from './wahaLabelsLogic';

export type LabelSyncResult =
  | { status: 'crm_only' }
  | { status: 'unchanged' }
  | { status: 'synced' }
  | { status: 'error'; message: string };

const sessionCache = new Map<string, string | null>();

/** Nome della sessione WAHA della config (null se non e' una config WAHA). */
async function sessionForConfig(configId: string): Promise<string | null> {
  if (sessionCache.has(configId)) return sessionCache.get(configId) ?? null;
  const { data, error } = await supabase
    .from('api_configurations')
    .select('service_name, configuration')
    .eq('id', configId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  const row = data as { service_name?: string; configuration?: { session?: string } | null } | null;
  const session = row?.service_name === 'waha' && row.configuration?.session ? row.configuration.session : null;
  sessionCache.set(configId, session);
  return session;
}

async function wahaJson(method: 'GET' | 'PUT', path: string, body?: unknown): Promise<unknown> {
  const res = await callWahaProxy({ method, path, body });
  if (!res.ok) throw new Error(`WhatsApp ha risposto ${res.status}`);
  const type = res.headers.get('content-type') || '';
  return res.status === 204 || !type.includes('application/json') ? undefined : res.json();
}

const chatPath = (session: string, chatId: string) =>
  `/api/${session}/labels/chats/${encodeURIComponent(chatId)}/`;

/**
 * Riporta sul telefono l'assegnazione (add=true) o la rimozione di un'etichetta del CRM.
 * Non solleva: l'esito serve solo per avvisare l'utente.
 */
export async function syncChatLabelToWhatsApp(
  label: LinkableLabel | undefined,
  chatJid: string,
  add: boolean,
  chatConfigId: string | null | undefined,
): Promise<LabelSyncResult> {
  // Solo etichette collegate al numero della chat aperta (config_id uguale): le altre restano nel CRM.
  if (!shouldSyncLabelToWhatsApp(label, chatJid, chatConfigId) || !isLinkedLabel(label)) return { status: 'crm_only' };
  try {
    const session = await sessionForConfig(label.config_id);
    if (!session) return { status: 'crm_only' };
    const current = parseWahaLabels(await wahaJson('GET', chatPath(session, chatJid))).map((l) => l.id);
    const next = nextWahaLabelIds(current, label.waha_label_id, add);
    if (sameIdSet(current, next)) return { status: 'unchanged' };
    await wahaJson('PUT', chatPath(session, chatJid), { labels: next.map((id) => ({ id })) });
    return { status: 'synced' };
  } catch (err) {
    return { status: 'error', message: err instanceof Error ? err.message : String(err) };
  }
}

export interface ImportResult {
  labels: number;
  created: number;
  assignments: number;
}

async function resolveLid(jid: string, configId: string): Promise<string> {
  if (!jid.endsWith('@lid')) return toCrmChatJid(jid);
  const { data } = await supabase
    .from('whatsapp_chat_aliases')
    .select('chat_key')
    .eq('config_id', configId)
    .eq('alias_jid', jid)
    .maybeSingle();
  const key = (data as { chat_key?: string } | null)?.chat_key ?? '';
  return key.startsWith('p:') ? `${key.slice(2)}@c.us` : jid;
}

/**
 * Importa le etichette WhatsApp Business del numero e le chat a cui sono assegnate.
 * Idempotente: rilanciarla aggiorna i nomi e aggiunge solo le assegnazioni mancanti.
 * Non toglie nulla nel CRM e non scrive nulla sul telefono.
 */
export async function importWahaLabels(organizationId: string, configId: string): Promise<ImportResult> {
  const session = await sessionForConfig(configId);
  if (!session) throw new Error('Questo numero non e\' collegato con WAHA: etichette non disponibili');

  let labels;
  try {
    labels = parseWahaLabels(await wahaJson('GET', `/api/${session}/labels`));
  } catch {
    throw new Error('WhatsApp non ha restituito etichette: servono un account WhatsApp Business e il numero connesso');
  }

  const { data: rowsData, error: rowsError } = await supabase
    .from('whatsapp_chat_labels')
    .select('id, name, color, config_id, waha_label_id, origin')
    .eq('organization_id', organizationId);
  if (rowsError) throw new Error(rowsError.message);
  const rows = (rowsData ?? []) as LinkableLabel[];

  let created = 0;
  let assignments = 0;
  for (const incoming of labels) {
    const plan = planImportLabel(incoming, configId, rows);
    let labelId: string;
    if (plan.action === 'create') {
      const { data, error } = await supabase
        .from('whatsapp_chat_labels')
        .insert({ organization_id: organizationId, ...plan.row })
        .select('id, name, color, config_id, waha_label_id, origin')
        .single();
      if (error || !data) throw new Error(error?.message || 'Creazione etichetta non riuscita');
      rows.push(data as LinkableLabel);
      labelId = (data as LinkableLabel).id;
      created++;
    } else {
      labelId = plan.id;
      if (plan.action !== 'none') {
        const { error } = await supabase
          .from('whatsapp_chat_labels')
          .update(plan.patch)
          .eq('id', plan.id)
          .eq('organization_id', organizationId);
        if (error) throw new Error(error.message);
        const idx = rows.findIndex((r) => r.id === plan.id);
        if (idx >= 0) rows[idx] = { ...rows[idx], ...plan.patch } as LinkableLabel;
      }
    }

    let chatIds: string[] = [];
    try {
      chatIds = parseChatIds(await wahaJson('GET', `/api/${session}/labels/${encodeURIComponent(incoming.id)}/chats`));
    } catch {
      continue; // etichetta senza chat leggibili: si passa alla prossima
    }
    const jids = [...new Set(await Promise.all(chatIds.map((id) => resolveLid(id, configId))))];
    if (jids.length === 0) continue;
    const { error } = await supabase
      .from('whatsapp_chat_label_assignments')
      .upsert(
        jids.map((chat_jid) => ({ organization_id: organizationId, chat_jid, label_id: labelId })),
        { onConflict: 'organization_id,chat_jid,label_id', ignoreDuplicates: true },
      );
    if (error) throw new Error(error.message);
    assignments += jids.length;
  }
  return { labels: labels.length, created, assignments };
}
